import * as types from '@vue-storefront/core/modules/cart/store/mutation-types'
import { Logger } from '@vue-storefront/core/lib/logger'
import {
  prepareProductsToAdd,
  productsEquals,
  validateProduct,
  createDiffLog,
  notifications
} from '@vue-storefront/core/modules/cart/helpers'
import { cartHooksExecutors } from './../../hooks'
import config from 'config'
import { CartService } from '@vue-storefront/core/data-resolver'
import { loadStock, resolveStock, attachManageStock } from 'src/modules/vsf-inventory-sources/helpers/stockResolver'
import CartItem from '@vue-storefront/core/modules/cart/types/CartItem';
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'

const itemActions = {
  async configureItem (context, { product, configuration }) {
    const { commit, dispatch, getters } = context
    const variant = await dispatch('product/getProductVariant', {
      product,
      configuration
    }, { root: true })

    const itemWithSameSku = getters.getCartItems.find(item => item.sku === variant.sku)

    if (itemWithSameSku && product.sku !== variant.sku) {
      Logger.debug('Item with the same sku detected', 'cart', { sku: itemWithSameSku.sku })()
      commit(types.CART_DEL_ITEM, { product: itemWithSameSku })
      product.qty = parseInt(product.qty) + parseInt(itemWithSameSku.qty)
    }

    commit(types.CART_UPD_ITEM_PROPS, { product: { ...product, ...variant } })
  },
  updateItem ({ commit }, { product }) {
    commit(types.CART_UPD_ITEM_PROPS, { product })
  },
  getItem ({ getters }, { product }) {
    return getters.getCartItems.find(p => productsEquals(p, product))
  },
  async addItem ({ dispatch, commit }, { productToAdd, qty = null }) {
    EventBus.$emit('cart-loading')

    const { cartItem } = cartHooksExecutors.beforeAddToCart({ cartItem: productToAdd })
    commit(types.CART_ADDING_ITEM, { isAdding: true })
    const diffLog = createDiffLog()

    try {
      const product = prepareProductsToAdd(cartItem)[0]
      const errors = validateProduct(product)
      diffLog.pushNotifications(notifications.createNotifications({ type: 'error', messages: errors }))

      if (errors.length > 0) {
        return diffLog
      }

      commit(types.CART_ADD_ITEM, {
        product: { ...product },
        specifiedQty: qty
      })

      const clientItem = await dispatch('getItem', { product })
      const result = await dispatch('updateServerItem', { item: clientItem })
      cartHooksExecutors.afterAddToCart(result)

      if (result) {
        diffLog.pushNotification(notifications.productAddedToCart())
      }

      return diffLog
    } finally {
      commit(types.CART_ADDING_ITEM, { isAdding: false })
    }
  },
  async checkProductStatus ({ dispatch, getters, rootGetters }, { product }) {
    const record = getters.getCartItems.find(p => productsEquals(p, product))
    const qty = record ? record.qty + 1 : (product.qty ? product.qty : 1)
    const sku = product && product.sku
    if (!sku) {
      return {
        qty: 0,
        status: 'volatile'
      }
    }

    // Keep cart stock validation aligned with storefront stock display logic.
    await loadStock(product)
    await attachManageStock(product)
    const stock = resolveStock(product, true)
    const onlineQty = stock && stock.qty ? stock.qty : 0
    const pickupQty = stock && stock.qtyPickup ? stock.qtyPickup : 0
    const isPickup = !!rootGetters['inventorySources/getIsPickup']
    const backorders = !!(stock && stock.backorders)
    const unmanaged = stock && stock.manageStock === false
    const requestedQty = Number(qty || 1)
    const availableQty = isPickup ? (onlineQty + pickupQty) : onlineQty

    if (unmanaged || backorders || availableQty >= requestedQty) {
      return {
        qty: unmanaged ? requestedQty : availableQty,
        status: 'ok'
      }
    }

    return {
      qty: availableQty,
      status: 'out_of_stock'
    }
  },
  async addItems ({ commit, dispatch, getters }, { productsToAdd, forceServerSilence = false }) {
    let productIndex = 0
    const diffLog = createDiffLog()
    for (let product of productsToAdd) {
      const errors = validateProduct(product)
      diffLog.pushNotifications(notifications.createNotifications({ type: 'error', messages: errors }))

      if (errors.length === 0) {
        const { status } = await dispatch('checkProductStatus', { product })

        if (status === 'volatile' && !config.stock.allowOutOfStockInCart) {
          diffLog.pushNotification(notifications.unsafeQuantity())
        }
        if (status === 'out_of_stock') {
          diffLog.pushNotification(notifications.outOfStock())
        }

        if (status === 'ok' || status === 'volatile') {
          commit(types.CART_ADD_ITEM, {
            product: { ...product }
          })
        }
        if (productIndex === (productsToAdd.length - 1) && (!getters.isCartConnected || forceServerSilence)) {
          diffLog.pushNotification(notifications.productAddedToCart())
        }
        productIndex++
      }
    }

    let newDiffLog = await dispatch('create')
    if (newDiffLog !== undefined) {
      diffLog.merge(newDiffLog)
    }

    if (getters.isCartConnected && !forceServerSilence) {
      const syncDiffLog = await dispatch('sync', { forceClientState: true })

      if (!syncDiffLog.isEmpty()) {
        diffLog.merge(syncDiffLog)
      }
    }

    return diffLog
  },
  async removeItem ({ commit, dispatch, getters }, payload) {
    EventBus.$emit('cart-loading')

    const product = payload.product || payload
    const { cartItem } = cartHooksExecutors.beforeRemoveFromCart({ cartItem: product })
    Logger.info('Removing product from cart', 'cart')()

    commit(types.CART_DEL_ITEM, { product: cartItem })

    if (cartItem.server_item_id) {
      const serverItem = {
        sku: cartItem.sku,
        item_id: cartItem.server_item_id
      } as any as CartItem
      Logger.log('Removing server item' + serverItem.sku + serverItem.item_id, 'cart')()
      const resp = await CartService.deleteItem(getters.getCartToken, serverItem)
      cartHooksExecutors.afterRemoveFromCart(resp)
      dispatch('syncTotals')
      return resp
    }
  },
  async restoreQuantity ({ dispatch }, { product }) {
    const currentCartItem = await dispatch('getItem', { product })
    if (currentCartItem) {
      Logger.log('Restoring qty after error' + product.sku + currentCartItem.prev_qty, 'cart')()
      if (currentCartItem.prev_qty > 0) {
        await dispatch('updateItem', {
          product: {
            ...product,
            qty: currentCartItem.prev_qty
          }
        })
        EventBus.$emit('cart-after-itemchanged', { item: currentCartItem })
      } else {
        await dispatch('removeItem', { product: currentCartItem })
      }
    }
  },
  async updateQuantity ({ commit, dispatch }, { product, qty }) {
    EventBus.$emit('cart-loading')
    await dispatch('addItem', { productToAdd: { ...product }, qty })
  }
}

export default itemActions
